import { FullState } from '../../store';
import { createSelector } from 'reselect';
import {
  FlyingPageState,
  FlightDetail,
  FlyingPageFilters,
  AircraftTypeEnum,
  FlightStatusFilterEnum
} from '../../types/flyingPage';
import { DefaultRootState } from 'react-redux';
import { getSelectedTracksIds, getTracksData } from './aircraftData';
import { getUserData } from './userData';
import { UserData } from '../types';
import { fromTimestamp } from '../../helpers/formatTime';

export const getFlyingPageState = (state: FullState): FlyingPageState => state.flyingPage;

export const getLatestFlightDetails = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): FlightDetail[] => flyingPage.latestFlightDetails
);

export const getFlyingPageFilters = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): FlyingPageFilters => flyingPage.filters
);

export const getFlyingPageFilterText = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): string | undefined => flyingPage.filters.filterText
);

// TODO reimplement this function to retun flyingPage.selectedFlightAircraftId once we refactor components in packages/@spidertracks/map/src/components/Flying/Map/GoogleMap/AircraftTracks/AircraftTrackContainer.js to use the new store
export const getSelectedFlightAircraftId = createSelector(
  [getTracksData, getSelectedTracksIds],
  (trackData, selectedTrackIds): string => {
    if (selectedTrackIds[0]) {
      return trackData[selectedTrackIds[0]]?.aircraft.id;
    } else {
      return '';
    }
  }
);

export const getFlightDetailsByFilter = (state: DefaultRootState, filters: FlyingPageFilters) =>
  createSelector(
    [getFlyingPageState, getUserData],
    (flyingPage: FlyingPageState, userData: UserData): FlightDetail[] => {
      const { timezone, orgs } = userData;
      const { filterText } = filters;
      const { showFiltersHideSearch } = flyingPage;

      let filteredFlightDetails = flyingPage.latestFlightDetails;
      if (filterText) {
        filteredFlightDetails = flyingPage.latestFlightDetails.filter(flightDetail =>
          flightDetail.aircraft.registration.toLowerCase().includes(filterText.toLowerCase())
        );
      }
      if (showFiltersHideSearch && filters.aircraftType && filters.aircraftType[0] !== '*') {
        filteredFlightDetails = filteredFlightDetails.filter(flightDetail =>
          filters.aircraftType.includes(flightDetail.aircraft.type)
        );
      }
      if (showFiltersHideSearch && filters.organisationIds && filters.organisationIds[0] !== '*') {
        filteredFlightDetails = filteredFlightDetails.filter(flightDetail =>
          filters.organisationIds.includes(flightDetail.track.aircraft.org?.id ?? '')
        );
      } else {
        filteredFlightDetails = filteredFlightDetails.filter(flightDetail =>
          orgs.map(org => org.org.id).includes(flightDetail.track.aircraft.org?.id ?? '')
        );
      }

      switch (filters.status) {
        case FlightStatusFilterEnum.NOW:
          filteredFlightDetails = filteredFlightDetails.filter(
            flightDetail => flightDetail.aircraft.isActive === true
          );
          break;
        case FlightStatusFilterEnum.TODAY:
          filteredFlightDetails = filteredFlightDetails.filter(flightDetail => {
            const localMomentInTimezone = fromTimestamp(flightDetail.track.departedTime).tz(
              timezone
            );
            return localMomentInTimezone.isSame(new Date(), 'day');
          });
          break;
        case FlightStatusFilterEnum.LAST24H:
          filteredFlightDetails = filteredFlightDetails.filter(
            flightDetail => flightDetail.track.departedTime >= Date.now() - 86400000
          );
          break;
        case FlightStatusFilterEnum.LAST7D:
          filteredFlightDetails = filteredFlightDetails.filter(
            flightDetail => flightDetail.track.departedTime >= Date.now() - 86400000 * 8 // Flights from past 8 days are returned to acommodate maximum timezone difference
          );
          break;
      }

      return filteredFlightDetails;
    }
  );

export const getFlightDetailsByAircraftId = (state: DefaultRootState, aircraftId: string) =>
  createSelector([getFlyingPageState], (flyingPage: FlyingPageState): FlightDetail | undefined => {
    return flyingPage.latestFlightDetails.find(detail => detail.aircraftId === aircraftId);
  });

export const getLoading = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): boolean => flyingPage.loading
);

export const getShowFiltersHideSearch = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): boolean => flyingPage.showFiltersHideSearch
);

export const getShowAircraftListPanel = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): boolean => flyingPage.showAircraftListPanel
);

export const getAircraftTypeFilter = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): string[] => flyingPage.filters.aircraftType
);

export const getOrgFilter = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): string[] => flyingPage.filters.organisationIds
);
export const getStatusFilter = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): FlightStatusFilterEnum => flyingPage.filters.status
);

export const getAircraftTypes = createSelector(
  [getFlyingPageState],
  (flyingPage: FlyingPageState): AircraftTypeEnum[] => {
    const aircraftTypeSets: Set<AircraftTypeEnum> = new Set();
    flyingPage.latestFlightDetails.forEach((flightDetail: FlightDetail) => {
      const { type } = flightDetail.aircraft;
      if (type) {
        aircraftTypeSets.add(type);
      }
    });
    return [...aircraftTypeSets];
  }
);
